import { graphql } from '@lib/graphql'
import { execute } from '@lib/graphql/execute'
import { ProductGridTileFragment } from '@lib/graphql/graphql'
import { QueryConfig } from '@lib/react-query'
import { keepPreviousData, queryOptions, useQuery } from '@tanstack/react-query'
import { CHANNEL, getChannelId } from 'config'

// Query for fetching product tiles with all variants
const fetchAllVariantsQuery = graphql(`
  query FetchProductTilesWithVariants(
    $channelId: ID!
    $after: String
    $currency: String
    $query: String
  ) {
    products(
      first: 100
      after: $after
      query: $query
      currency: $currency
      channelId: $channelId
    ) {
      edges {
        node {
          id
          slug
          name
          tags
          variantsCount
          variants(first: 1) {
            edges {
              node {
                ...ProductGridTileVariant
              }
            }
            pageInfo {
              hasNextPage
              endCursor
            }
          }
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`)

// Query for fetching product tiles with only hero variants
const fetchHeroVariantQuery = graphql(`
  query FetchHeroVariantTiles(
    $channelId: ID!
    $after: String
    $currency: String
    $query: String
  ) {
    products(
      first: 100
      after: $after
      query: $query
      currency: $currency
      channelId: $channelId
    ) {
      edges {
        node {
          ...ProductGridTile
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
  fragment ProductGridTile on Product {
    id
    slug
    name
    tags
    variantsCount
    heroVariant {
      ...ProductGridTileVariant
    }
  }
  fragment ProductGridTileVariant on ProductVariant {
    id
    sku
    media(first: 10) {
      edges {
        node {
          src
          fileName
        }
      }
    }
    metadata {
      key
      value
    }
    availability {
      availableForPurchase
      availableQuantity
      stockPolicy
    }
    price {
      value {
        centAmount
        currencyCode
        fractionDigits
      }
      discountedPrice {
        discount {
          value {
            __typename
            ... on ProductDiscountRelativeValue {
              factor
            }
          }
        }
        value {
          centAmount
          currencyCode
          fractionDigits
        }
      }
    }
  }
`)

// Query for fetching additional variants of a product
const fetchAdditionalVariantsQuery = graphql(`
  query FetchAdditionalVariants(
    $id: ID!
    $channelId: ID!
    $currency: String
    $after: String
  ) {
    product(id: $id, currency: $currency, channelId: $channelId) {
      id
      variants(first: 50, after: $after) {
        edges {
          node {
            id
            sku
            media {
              edges {
                node {
                  src
                  fileName
                }
              }
            }
            metadata {
              key
              value
            }
            availability {
              availableForPurchase
              availableQuantity
              stockPolicy
            }
            price {
              discountedPrice {
                discount {
                  value {
                    __typename
                    ... on ProductDiscountRelativeValue {
                      factor
                    }
                  }
                }
                value {
                  centAmount
                  currencyCode
                  fractionDigits
                }
              }
              value {
                centAmount
                currencyCode
                fractionDigits
              }
            }
          }
        }
        pageInfo {
          hasNextPage
          endCursor
        }
      }
    }
  }
`)

// Fetch additional variants for a product
const fetchAllVariantsForProduct = async (
  productId: string,
  channelId: string,
  currency: string,
  intialEndCursor: string,
): Promise<any[]> => {
  let after = intialEndCursor
  const allVariants: any[] = []

  while (true) {
    console.log('Fetching additional variants for product:', productId, after)
    const response = await execute(fetchAdditionalVariantsQuery, {
      id: productId,
      channelId,
      currency,
      after,
    })

    if (!response.product.variants) {
      break
    }
    const { edges, pageInfo } = response.product.variants

    allVariants.push(...edges.map((edge) => edge.node))

    if (!pageInfo.hasNextPage) break
    after = pageInfo.endCursor
  }

  return allVariants
}

// Fetch product tiles (recursively handles pagination)
const fetchProductTiles = async (
  channelId: string,
  currency: string,
  query: string | null,
  after: string | null = null,
  includeAllVariants: boolean = false,
  collectedTiles: ProductGridTileFragment[] = [],
): Promise<ProductGridTileFragment[]> => {
  const queryDocument = includeAllVariants
    ? fetchAllVariantsQuery
    : fetchHeroVariantQuery

  //@ts-ignore -- TODO REMAKE THIS ANYWAYS
  const response = await execute(queryDocument, {
    channelId,
    after,
    currency,
    query,
  })

  if (!response.products) {
    return collectedTiles
  }
  const { edges, pageInfo } = response.products

  const products = await Promise.all(
    edges.map(async (edge) => {
      const product = edge.node

      // If fetching all variants and there are more, fetch additional variants
      if (includeAllVariants && product.variants?.pageInfo.hasNextPage) {
        const additionalVariants = await fetchAllVariantsForProduct(
          product.id,
          channelId,
          currency,
          product.variants.pageInfo.endCursor,
        )
        const mergedVariants = [
          ...product.variants.edges.map((edge) => edge.node),
          ...additionalVariants,
        ]
        product.variants.edges = mergedVariants.map((variant) => ({
          node: variant,
        }))
      }

      return product
    }),
  )

  collectedTiles.push(...products)

  if (pageInfo.hasNextPage) {
    return fetchProductTiles(
      channelId,
      currency,
      query,
      pageInfo.endCursor,
      includeAllVariants,
      collectedTiles,
    )
  }

  return collectedTiles
}

type ProductTilesQueryVariables = {
  channel: CHANNEL
  currency: string
  query: string | null
  includeAllVariants?: boolean
}

// Main function to get product tiles
export const getProductTiles = async ({
  channel,
  currency,
  query,
  includeAllVariants = false,
}: ProductTilesQueryVariables): Promise<ProductGridTileFragment[]> => {
  return fetchProductTiles(
    getChannelId(channel),
    currency,
    query,
    null,
    includeAllVariants,
  )
}

const getProductTilesQueryOptions = (variables: ProductTilesQueryVariables) => {
  return queryOptions({
    queryKey: ['product-tiles', variables],
    queryFn: () => getProductTiles(variables),
    placeholderData: keepPreviousData,
  })
}

export const useProductTiles = ({
  variables,
  queryConfig,
}: {
  variables?: ProductTilesQueryVariables
  queryConfig?: QueryConfig<typeof getProductTilesQueryOptions>
} = {}) => {
  return useQuery({
    ...getProductTilesQueryOptions(variables),
    ...queryConfig,
  })
}
